import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { getTaxYears } from '@/utils/handlers';

import { ContributionAmountSideKey } from '../../ContributionEmployeesSettings/ContributionEmployeesSettings.types';
import { TaxYearsLimitsIRS } from '../../ContributionEmployeesSettings/types';
import { EMPTY_ERROR_MESSAGE } from '../../ContributionEmployeesSettings/utils/getErrorMessage';
import { areAmountsValidBySum } from '../common';
import { ManualContributionCoveragePeriodsByEmployeeIdPlanCode } from '../types';
import { ChangedContributionRecord } from '../useChangedContributionRecords.store';

const availableTaxYears = getTaxYears();

const CONTRIBUTION_PERIOD_VALIDATION_ERROR_TEXT = 'This plan is not active on the contribution date.';

export const getCoveragePeriodError = (
  record: ChangedContributionRecord,
  manualContributionCoveragePeriods?: ManualContributionCoveragePeriodsByEmployeeIdPlanCode,
) => {
  const hasNoDataForValidation = record?.isDeleted ||
    !manualContributionCoveragePeriods ||
    !record?.planCode ||
    !record?.employeeId;
  if (hasNoDataForValidation) {
    return '';
  }
  if (!record?.contributionDateOn) {
    return EMPTY_ERROR_MESSAGE;
  }
  if (!dayjs(record?.contributionDateOn).isValid()) {
    return 'The date is not valid.';
  }
  const employeeIdPlanCodeKey = `${record.employeeId},${record.planCode}`;
  const coveragePeriods = manualContributionCoveragePeriods[employeeIdPlanCodeKey];

  if (!coveragePeriods?.length) {
    return '';
  }

  const isValidContributionDateOn = coveragePeriods.some((period) => {
    const hasNoPeriodDates = !period?.coverage_date_start && !period?.coverage_date_end;
    if (hasNoPeriodDates) return true;
    const hasNoStartDate = !period?.coverage_date_start || !dayjs(period?.coverage_date_start).isValid();
    const isValidByStartDate = hasNoStartDate
      || dayjs(record.contributionDateOn).valueOf() >= dayjs(period?.coverage_date_start).valueOf();
    if (!isValidByStartDate) return false;
    const hasNoEndDate = !period?.coverage_date_end || !dayjs(period?.coverage_date_end).isValid();
    const isValidByEndDate = hasNoEndDate
      || dayjs(record.contributionDateOn).valueOf() <= dayjs(period?.coverage_date_end).valueOf();
    if (!isValidByEndDate) return false;
    return true;
  });

  return isValidContributionDateOn ? '' : CONTRIBUTION_PERIOD_VALIDATION_ERROR_TEXT;
};

export const getAmountsError = (record: ChangedContributionRecord, limits: TaxYearsLimitsIRS) => {
  if (record?.isDeleted) {
    return '';
  }
  const errorMessage = areAmountsValidBySum(record?.employeeContributionAmount, record?.employerContributionAmount)
    ? ''
    : EMPTY_ERROR_MESSAGE;

  if (errorMessage) return errorMessage;

  if (!record || !record?.taxYear || !record?.employeeId || !record?.planId) {
    return '';
  }

  const limit = limits?.[record?.taxYear]?.[record?.employeeId]?.[record?.planId];
  if (typeof limit !== 'number') {
    return '';
  }

  const erAmount = record?.[ContributionAmountSideKey.EmployeeContributionAmount] || '0';
  const emAmount = record?.[ContributionAmountSideKey.EmployerContributionAmount] || '0';
  const contributionSum = toNumber(erAmount) + toNumber(emAmount);
  if (!contributionSum || contributionSum <= limit) {
    return '';
  }

  return `The sum is more than available to contribute for this employee. To reach limit: $${limit}.`;
};

export const getTaxYearError = (taxYear?: string) => availableTaxYears
  ?.some((item) => item === taxYear)
    ? ''
    : 'The tax year is not specified correctly';
